import React from 'react';
import { Button } from '@/components/ui/button';
import { Building, Users, ArrowRight } from 'lucide-react';

interface OnboardingChoiceProps {
  onSelect: (choice: 'single-org' | 'multi-org') => void;
}

export function OnboardingChoice({ onSelect }: OnboardingChoiceProps) {
  return (
    <div className="max-w-5xl mx-auto px-4">
      <div className="text-center mb-10">
        <h1 className="text-4xl font-bold tracking-tight text-gray-900 mb-3">Choose Your Path Forward</h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Select the option that best describes your role to get personalized tools and insights for your healthcare management needs.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        <ChoiceCard
          icon={<Building className="h-5 w-5" />}
          title="Single Organization"
          description="Perfect for healthcare providers managing their own practice data, quality measures, and reporting requirements."
          features={[
            "Direct data management",
            "Simplified reporting workflow",
            "Single practice dashboard"
          ]}
          onClick={() => onSelect('single-org')}
        />
        <ChoiceCard
          icon={<Users className="h-5 w-5" />}
          title="Multiple Organizations"
          description="Designed for consultants and power users who need to manage data across multiple client organizations and practices."
          features={[
            "Multi-client dashboard",
            "Advanced user permissions",
            "Consolidated reporting"
          ]}
          onClick={() => onSelect('multi-org')}
        />
      </div>
    </div>
  );
}

interface ChoiceCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  features: string[];
  onClick: () => void;
}

function ChoiceCard({ icon, title, description, features, onClick }: ChoiceCardProps) {
  return (
    <div className="bg-white rounded-lg p-6 flex flex-col h-full border border-gray-200 hover:border-gray-300 transition-colors relative overflow-hidden">
      {/* Decorative circle in top right - BIGGER */}
      <div className="absolute -top-12 -right-12 w-32 h-32 bg-gray-100 rounded-full opacity-40" />
      
      <div className="flex items-center gap-3 mb-5 relative">
        <div className="bg-gray-100 rounded-lg p-2.5">
          <div className="text-gray-700">
            {icon}
          </div>
        </div>
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
      </div>
      
      <p className="text-gray-600 text-sm mb-5 leading-relaxed h-[4.5rem]">
        {description}
      </p>
      
      <ul className="space-y-2.5 mb-6 flex-grow">
        {features.map((feature, index) => (
          <li key={index} className="flex items-center">
            <span className="text-gray-700 mr-3 text-2xl font-bold leading-none">•</span>
            <span className="text-gray-600 text-sm">{feature}</span>
          </li>
        ))}
      </ul>
      
      <button 
        onClick={onClick}
        className="w-full bg-gray-900 hover:bg-gray-800 text-white font-medium py-2.5 rounded-md flex items-center justify-center gap-2 group transition-colors text-sm"
      >
        Get Started
        <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
      </button>
    </div>
  );
}
